import React, { createContext, useState, useEffect } from 'react';
import { supabase } from '../supabase/supabaseClient';

export const ProductContext = createContext();

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [productCampaigns, setProductCampaigns] = useState({});
  const [productMetrics, setProductMetrics] = useState({});
  const [loading, setLoading] = useState(true);

  // Carregar produtos e campanhas
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Buscar produtos
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('*')
          .order('created_at', { ascending: false });

        if (productsError) {
          console.error('Erro ao buscar produtos:', productsError);
          return;
        }

        if (productsData) {
          setProducts(productsData);

          // Buscar campanhas de cada produto
          const campaignsMap = {};
          for (const product of productsData) {
            const { data: campaignsData, error: campaignsError } = await supabase
              .from('product_campaigns')
              .select('*')
              .eq('product_id', product.id)
              .order('date', { ascending: false });

            if (!campaignsError && campaignsData) {
              campaignsMap[product.id] = campaignsData;
            }
          }
          setProductCampaigns(campaignsMap);
        }
      } catch (err) {
        console.error('Erro ao buscar dados:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const productsSub = supabase
      .channel('products-watch')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, fetchData)
      .subscribe();

    const campaignsSub = supabase
      .channel('campaigns-watch')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'product_campaigns' }, fetchData)
      .subscribe();

    return () => {
      supabase.removeChannel(productsSub);
      supabase.removeChannel(campaignsSub);
    };
  }, []);

  // Recalcular métricas sempre que produtos ou campanhas mudarem
  useEffect(() => {
    const metrics = {};

    products.forEach((product) => {
      const campaigns = productCampaigns[product.id] || [];

      const totalInvestment = campaigns.reduce((sum, c) => sum + parseFloat(c.investment || 0), 0);
      const totalRevenue = campaigns.reduce((sum, c) => sum + parseFloat(c.revenue || 0), 0);
      const totalSales = campaigns.reduce((sum, c) => sum + parseInt(c.sales || 0), 0);
      const totalLeads = campaigns.reduce((sum, c) => sum + parseInt(c.leads || 0), 0);

      const roi = totalInvestment > 0 ? ((totalRevenue - totalInvestment) / totalInvestment) * 100 : 0;
      const cpa = totalSales > 0 ? totalInvestment / totalSales : 0;
      const conversionRate = totalLeads > 0 ? (totalSales / totalLeads) * 100 : 0;

      metrics[product.id] = {
        totalInvestment,
        totalRevenue,
        totalSales,
        totalLeads,
        roi,
        cpa,
        conversionRate
      };
    });

    setProductMetrics(metrics);
  }, [products, productCampaigns]);

  // === CRUD: Produto ===
  const addProduct = async (productData) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .insert({
          name: productData.name,
          price: parseFloat(productData.price),
          category: productData.category,
          description: productData.description || '',
          status: productData.status,
          launch_date: productData.launchDate,
          created_at: new Date().toISOString()
        })
        .select();

      if (error) {
        console.error('Erro ao adicionar produto:', error);
        return null;
      }

      return data[0];
    } catch (err) {
      console.error('Erro ao adicionar produto:', err);
      return null;
    }
  };

  const updateProduct = async (productId, updatedData) => {
    try {
      const { error } = await supabase
        .from('products')
        .update({
          name: updatedData.name,
          price: parseFloat(updatedData.price),
          category: updatedData.category,
          description: updatedData.description || '',
          status: updatedData.status,
          launch_date: updatedData.launchDate,
          updated_at: new Date().toISOString()
        })
        .eq('id', productId);

      if (error) {
        console.error('Erro ao atualizar produto:', error);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Erro ao atualizar produto:', err);
      return false;
    }
  };

  const removeProduct = async (productId) => {
    try {
      const { error: campErr } = await supabase
        .from('product_campaigns')
        .delete()
        .eq('product_id', productId);

      if (campErr) {
        console.error('Erro ao deletar campanhas:', campErr);
        return false;
      }

      const { error } = await supabase.from('products').delete().eq('id', productId);

      if (error) {
        console.error('Erro ao remover produto:', error);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Erro ao remover produto:', err);
      return false;
    }
  };

  // === CRUD: Campanhas ===
  const addProductCampaign = async (productId, campaignData) => {
    try {
      const formattedDate = campaignData.date?.split('T')[0] || new Date().toISOString().split('T')[0];
      console.log("📦 Enviando campanha:", { ...campaignData, date: formattedDate });

      const { data, error } = await supabase
        .from('product_campaigns')
        .insert({
          product_id: productId,
          date: formattedDate,
          channel: campaignData.channel,
          investment: parseFloat(campaignData.investment),
          leads: parseInt(campaignData.leads || 0),
          sales: parseInt(campaignData.sales || 0),
          revenue: parseFloat(campaignData.revenue || 0),
          created_at: new Date().toISOString()
        })
        .select();

      if (error) {
        console.error('Erro ao adicionar campanha:', error);
        return null;
      }

      return data[0];
    } catch (err) {
      console.error('Erro ao adicionar campanha:', err);
      return null;
    }
  };

  const updateProductCampaign = async (productId, campaignId, updatedData) => {
    try {
      const { error } = await supabase
        .from('product_campaigns')
        .update({
          date: updatedData.date,
          channel: updatedData.channel,
          investment: parseFloat(updatedData.investment),
          leads: parseInt(updatedData.leads || 0),
          sales: parseInt(updatedData.sales || 0),
          revenue: parseFloat(updatedData.revenue || 0),
          updated_at: new Date().toISOString()
        })
        .eq('id', campaignId);

      if (error) {
        console.error('Erro ao atualizar campanha:', error);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Erro ao atualizar campanha:', err);
      return false;
    }
  };

  const removeProductCampaign = async (productId, campaignId) => {
    try {
      const { error } = await supabase
        .from('product_campaigns')
        .delete()
        .eq('id', campaignId);

      if (error) {
        console.error('Erro ao remover campanha:', error);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Erro ao remover campanha:', err);
      return false;
    }
  };

  // Getters
  const getProductCampaigns = (productId) => productCampaigns[productId] || [];

  const getProductMetrics = (productId) => productMetrics[productId] || {
    totalInvestment: 0,
    totalRevenue: 0,
    totalSales: 0,
    totalLeads: 0,
    roi: 0,
    cpa: 0,
    conversionRate: 0
  };

  const getOverallProductMetrics = () => {
    const overall = {
      totalInvestment: 0,
      totalRevenue: 0,
      totalSales: 0,
      totalLeads: 0,
      productCount: products.length
    };

    Object.values(productMetrics).forEach(metrics => {
      overall.totalInvestment += metrics.totalInvestment;
      overall.totalRevenue += metrics.totalRevenue;
      overall.totalSales += metrics.totalSales;
      overall.totalLeads += metrics.totalLeads;
    });

    overall.roi = overall.totalInvestment > 0 ? ((overall.totalRevenue - overall.totalInvestment) / overall.totalInvestment) * 100 : 0;
    overall.cpa = overall.totalSales > 0 ? overall.totalInvestment / overall.totalSales : 0;
    overall.conversionRate = overall.totalLeads > 0 ? (overall.totalSales / overall.totalLeads) * 100 : 0;

    return overall;
  };

  return (
    <ProductContext.Provider
      value={{
        products,
        loading,
        addProduct,
        removeProduct,
        updateProduct,
        addProductCampaign,
        removeProductCampaign,
        updateProductCampaign,
        getProductCampaigns,
        getProductMetrics,
        getOverallProductMetrics
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
